import {
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Link,
  Image,
  Divider,
} from "@heroui/react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconRotateClockwise,
} from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { CardItem } from "@/interfaces/CardItem";
import { getGameHandler } from "@/games";
import { SupportedGames } from "@/types/game";
import { PriceHistory } from "@/models/price-history";
import PriceHistoryChart from "@/components/PriceHistory/PriceHistoryChart";

function formatCardName(
  name: string | undefined,
  gameName: SupportedGames,
): string | undefined {
  if (!name) return undefined;
  // For Riftbound, only show the part before the comma
  if (gameName === "riftbound") {
    return name.split(",")[0].trim();
  }
  return name;
}

interface GenericCardModalWithNavigationProps {
  isOpen: boolean;
  onOpenChange: () => void;
  cardData: CardItem | undefined;
  gameName: SupportedGames;
  tcgplayerId?: string | null;
  cardPriceHistory?: PriceHistory[];
  priceIsLoaded?: boolean;
  aaCardId?: string | null;
  overrideName?: string;
  onNavigate?: (direction: "prev" | "next") => void;
  enableRotation?: boolean;
  overrideCardId?: string; // For showing ID even when card data is not found
}

export default function GenericCardModalWithNavigation({
  isOpen,
  onOpenChange,
  cardData,
  gameName,
  overrideName,
  tcgplayerId,
  cardPriceHistory,
  priceIsLoaded,
  aaCardId,
  onNavigate,
  enableRotation = false,
  overrideCardId,
}: GenericCardModalWithNavigationProps) {
  const [rotation, setRotation] = useState(enableRotation ? 90 : 0);

  // Reset rotation when card data changes (navigation)
  useEffect(() => {
    setRotation(enableRotation ? 90 : 0);
  }, [cardData?.id, aaCardId, tcgplayerId, enableRotation]);

  const handleOpenChange = () => {
    setRotation(enableRotation ? 90 : 0);
    onOpenChange();
  };
  const gameHandler = getGameHandler(gameName);
  const cardModalData = gameHandler.getCardModalData();
  let curCardData = cardData;
  const cardId = cardData?.id;

  if (!cardId && !aaCardId && !tcgplayerId && !overrideCardId) {
    return (
      <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 text-center">
                <div>No card data</div>
              </ModalHeader>
              <ModalBody>No card data available</ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  }

  // If we have overrideCardId but no actual card data, show a special message
  if (overrideCardId && !cardId && !aaCardId && !tcgplayerId) {
    return (
      <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 text-center">
                <div>Card not found</div>
                <div className="text-sm font-normal text-default-500">
                  {overrideCardId}
                </div>
              </ModalHeader>
              <ModalBody>
                <p>
                  Card data for <strong>{overrideCardId}</strong> could not be
                  found in the database.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  }

  if (tcgplayerId && !cardId) {
    curCardData = gameHandler.getTcgPlayerCardData(tcgplayerId) as CardItem;
  } else if (aaCardId && !cardId) {
    curCardData = gameHandler.getCardData(aaCardId) as CardItem;
  }

  const imgUrl = cardModalData.getCardImage(
    aaCardId || "",
    tcgplayerId || null,
  );
  const cardType = curCardData ? cardModalData.getCardType(curCardData) : "";
  const cardEffect = curCardData
    ? cardModalData.getCardEffect(curCardData)
    : "";
  const cardTrigger = curCardData
    ? cardModalData.getCardTrigger(curCardData)
    : undefined;

  const additionalChips = curCardData
    ? cardModalData.getAdditionalChips?.(curCardData)
    : [];
  const cardLink = curCardData
    ? cardModalData.getCardLink(curCardData, tcgplayerId || null)
    : "#";

  return (
    <Modal
      disableAnimation
      size="3xl"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      scrollBehavior={"outside"}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1 text-center">
              <div>
                {overrideName || formatCardName(curCardData?.name, gameName)}
              </div>
              {(cardId || aaCardId) && (
                <div className="text-sm font-normal text-default-500">
                  {aaCardId || cardId}
                </div>
              )}
            </ModalHeader>
            <ModalBody className="relative">
              {cardPriceHistory && priceIsLoaded !== undefined && (
                <div className="mb-2 md:mb-4">
                  <PriceHistoryChart
                    tcgplayerId={tcgplayerId || null}
                    cardPriceHistory={cardPriceHistory}
                    isLoaded={priceIsLoaded}
                  />
                </div>
              )}
              <div className="flex flex-col md:flex-row gap-4 relative">
                {/* Navigation buttons - Outside content area on desktop */}
                {onNavigate && (
                  <>
                    <Button
                      isIconOnly
                      size="lg"
                      className="hidden md:flex items-center justify-center absolute left-2 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 text-white hover:bg-black/70"
                      onPress={() => onNavigate("prev")}
                    >
                      <IconChevronLeft size={24} />
                    </Button>
                    <Button
                      isIconOnly
                      size="lg"
                      className="hidden md:flex items-center justify-center absolute right-2 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 text-white hover:bg-black/70"
                      onPress={() => onNavigate("next")}
                    >
                      <IconChevronRight size={24} />
                    </Button>
                  </>
                )}

                {/* Card Image - Left side on desktop, top on mobile */}
                <div className="flex justify-center min-h-[466px] md:min-h-0 relative md:w-1/2">
                  {imgUrl && (
                    <Image
                      alt={curCardData?.name}
                      src={imgUrl}
                      style={{
                        transform: `rotate(${rotation}deg)`,
                        transition: "none",
                      }}
                      className={`${
                        rotation % 180 !== 0
                          ? "max-w-full max-h-[400px] md:max-h-[500px] object-contain"
                          : ""
                      }`}
                    />
                  )}

                  {/* Rotate button */}
                  {enableRotation && (
                    <Button
                      isIconOnly
                      size="sm"
                      className="absolute top-2 right-2 z-20 bg-black/50 text-white hover:bg-black/70"
                      onPress={() => setRotation((prev) => prev + 90)}
                    >
                      <IconRotateClockwise size={16} />
                    </Button>
                  )}

                  {/* Navigation buttons on mobile - on the card */}
                  {onNavigate && (
                    <>
                      <Button
                        isIconOnly
                        size="lg"
                        className="md:hidden absolute -left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white hover:bg-black/70"
                        onPress={() => onNavigate("prev")}
                      >
                        <IconChevronLeft size={24} />
                      </Button>
                      <Button
                        isIconOnly
                        size="lg"
                        className="md:hidden absolute -right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white hover:bg-black/70"
                        onPress={() => onNavigate("next")}
                      >
                        <IconChevronRight size={24} />
                      </Button>
                    </>
                  )}
                </div>

                {/* Card Details - Right side on desktop, bottom on mobile */}
                <div className="flex flex-col gap-4 md:w-1/2">
                  <div className="flex justify-center md:justify-start gap-2 flex-wrap">
                    {/*
                      Chips come from the game's CardModalData - Naruto supplies
                      its traits through getAdditionalChips, so no game branch
                      is needed here.
                    */}
                    {cardType && <Chip>{cardType}</Chip>}
                    {additionalChips?.map((chip: string, index: number) => (
                      <Chip key={index}>{chip}</Chip>
                    ))}
                  </div>
                  <div className="leading-loose">
                    <div>{cardEffect}</div>
                    {cardTrigger && cardTrigger.length > 0 && (
                      <>
                        <Divider className="my-4" />
                        <div>{cardTrigger}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={onClose}>
                Close
              </Button>
              {tcgplayerId && (
                <Button color="primary" as={Link} href={cardLink}>
                  More Details
                </Button>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
